import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Container, Button, Grid2, Box } from '@mui/material';
import { Appointment } from '../../../../types';
import DatePicker from '../../../booking/DatePicker';
import TimeSlotPicker from '../../../booking/TimeSlotPicker';
import ErrorDialog from '../../../global/ErrorDialog';
import { dayjs } from '../../../../utils/dayjs-config';
import LoadingSpinner from '../../../global/LoadingSpinner';
import { useAdminData } from '../../../../contexts/AdminDataContext';

interface AppointmentCalendarAndSlotsProps {
    appointmentDuration: number;
    employeeId: string;
    appointment: Appointment;
    setAppointment: (appointment: Appointment) => void;
    scheduleCache?: Map<string, string>;
}

const AppointmentSelection: React.FC<AppointmentCalendarAndSlotsProps> = ({
    appointmentDuration,
    employeeId,
    appointment,
    setAppointment,
    scheduleCache,
}) => {
    const [availableSlots, setAvailableSlots] = useState<string[]>([]);
    const [openErrorDialog, setOpenErrorDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [currentStep, setCurrentStep] = useState(0); // Step starts at 0
    const steps = ['1', '2'];
    const goToNextStep = () => setCurrentStep((prev) => prev + 1); // Move forward in stepper

    const { scheduleQuery } = useAdminData();

    const resolvedSchedule = useMemo(() => scheduleCache ?? scheduleQuery.data ?? null, [scheduleCache, scheduleQuery.data]);
    const isLoadingSchedule = !resolvedSchedule && (scheduleQuery.isLoading || scheduleQuery.isFetching);
    const scheduleLoadError = scheduleQuery.isError ? (scheduleQuery.error as Error | undefined)?.message || 'Failed to load schedule.' : null;

    useEffect(() => {
        if (scheduleLoadError) {
            setErrorMessage(scheduleLoadError);
            setOpenErrorDialog(true);
        }
    }, [scheduleLoadError]);

    const handleErrorDialogClose = () => {
        setOpenErrorDialog(false);
        setErrorMessage(null);
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <>
                        {(employeeId && resolvedSchedule && appointment) && (
                        <DatePicker
                            appointment={appointment}
                            handleAppointment={setAppointment}
                            updateAppointment={true}
                            initialDate={dayjs.tz()}
                            appointmentDuration={appointmentDuration}
                            scheduleCache={resolvedSchedule}
                            disableDates={true}
                            setAvailableSlots={setAvailableSlots}
                            goToNextStep={goToNextStep}
                            minimumBookingNoticeHours={1}
                        />
                        )}
                    </>
                );
            case 1: 
                return (
                    <>
                        {(appointment && availableSlots.length > 0) && (
                        <TimeSlotPicker
                            appointment={appointment}
                            handleAppointment={setAppointment}
                            updateAppointment={true}
                            availableSlots={availableSlots}
                            goToNextStep={goToNextStep}
                            navigateToNextStep={false}
                        />
                        )}
                    </>
                );
        }
    }

    return (
        <Grid2 container spacing={2} direction="column" alignItems="center">

            <ErrorDialog open={openErrorDialog} onClose={handleErrorDialogClose} message={errorMessage} />

            {isLoadingSchedule ? (
                <LoadingSpinner/>
            ) : (
                <>   
                    <Container sx={{ my: 4 }}>
                        <Typography variant="h6" align="center" fontWeight={500} gutterBottom sx={{ mb: 1 }}>
                            Edit an Appointment
                        </Typography>
                        <Box sx={{mt: 3, mb: 2, display: 'flex', justifyContent: 'space-between' }}>
                            {
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setCurrentStep((prev) => prev - 1);
                                        }
                                    }
                                    disabled={
                                        (currentStep === 0)
                                    }
                                    sx ={{borderRadius: '4px', border: '2px solid #FFF', fontWeight: '600'}}
                                    >
                                    Back
                                </Button>
                            }
        
                            {/* No next button in the last step*/}
                            {currentStep < steps.length - 1 && 
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => setCurrentStep((prev) => prev + 1)}
                                    disabled={
                                        (currentStep === 0 && availableSlots.length === 0)
                                    }
                                    sx ={{borderRadius: '4px', border: '2px solid rgba(255,255,255,0.15)', fontWeight: '600' }}
                                >
                                Next
                                </Button>
                            }
                        </Box>
                            <hr
                                style={{
                                border: "none",
                                borderBottom: "2px solid rgba(0,0,0,0.2)",
                                boxShadow: "0 1px 5px rgba(0, 0, 0, 0.3)",
                                marginBottom: "10px",
                                width: "100%",
                                }}
                            />         
                        {renderStepContent()}
                    </Container>
                </>
            )}
        </Grid2>
    );
};

export default AppointmentSelection;
